import React from 'react';
import type { GetServerSidePropsContext } from 'next';
import { getSelectorsByUserAgent } from 'react-device-detect';
import Head from 'next/head';
import Header from '@/components/Header/Header';
import Footer from '@/components/Footer/Footer';
import { SolveTickets } from '@/components/PagesSections/FreshdeskIntegration/SolveTickets/SolveTickets';
import { SecondSection } from '@/components/PagesSections/FreshdeskIntegration/SecondSection/SecondSection';
import { AiForFreshdesk } from '@/components/PagesSections/FreshdeskIntegration/AiForFreshdesk/AiForFreshdesk';
import { AiAssistantFreshdesk } from '@/components/PagesSections/FreshdeskIntegration/AiAssistantFreshdesk/AiAssistantFreshdesk';
import { IntegrationCRM } from '@/components/PagesSections/FreshdeskIntegration/IntegrationCRM/IntegrationCRM';
import { EasyStartWithFreshdesk } from '@/components/PagesSections/FreshdeskIntegration/EasyStartWithFreshdesk/EasyStartWithFreshdesk';
import { PatentedTechnology } from '@/components/PagesSections/FreshdeskIntegration/PatentedTechnology/PatentedTechnology';
import { FAQFreshdesk } from '@/components/PagesSections/FreshdeskIntegration/FAQFreshdesk/FAQFreshdesk';
import { SuperChargeWithAgent } from '@/components/PagesSections/FreshdeskIntegration/SuperChargeWithAgent/SuperChargeWithAgent';
import { MediaRecognition } from '@/components/MediaRecognition/MediaRecognition';
import { Loader } from '@/components/Loader/Loader';
import { useLoader } from '@/hooks/useLoader';
import { PATH_INTEGRATION } from '@/constants/spa-routes';
import { TransparentPricing } from '@/components/PagesSections/FreshdeskIntegration/TransparentPricing/TransparentPricing';
import Script from 'next/script';
import { BoostYourSupport } from '@/components/PagesSections/FreshdeskIntegration/BoostYourSupport/BoostYourSupport';
import { ConnectRealGenerative } from '@/components/PagesSections/FreshdeskIntegration/ConnectRealGenerative/ConnectRealGenerative';
import { EnsuringDataPrivacy } from '@/components/PagesSections/FreshdeskIntegration/EnsuringDataPrivacy/EnsuringDataPrivacy';

interface FreshdeskIntegrationProps {
  isMobile: boolean;
  originUrl: string;
}

export default function FreshdeskIntegration({ originUrl, isMobile }: FreshdeskIntegrationProps) {
  const loading = useLoader();

  return (
    <>
      <Head>
        <title>Turbocharge Customer Support with AI for Freshdesk</title>
        <meta
          key="subject"
          name="subject"
          content="Generative AI for Efficient Freshdesk Support."
        ></meta>
        <meta
          key="description"
          name="description"
          content="Boost your Freshdesk customer support with patented AI copilot. Increase efficiency, speed up responses, and resolve tickets faster."
        />
        <link
          key="canonical"
          rel="canonical"
          href={`${originUrl}${PATH_INTEGRATION.INTEGRATION_FRESHDESK}`}
        />
        <meta key="ogtitle" property="og:title" content="CoSupport AI Freshdesk Integration"></meta>
        <meta key="ogtype" property="og:type" content="website" />
        <meta key="oglocale" property="og:locale" content="en_US" />
        <meta
          key="ogurl"
          property="og:url"
          content={`${originUrl}${PATH_INTEGRATION.INTEGRATION_FRESHDESK}`}
        />
        <meta
          key="ogimage"
          property="og:image"
          content={`${originUrl}/img/open-graph/freshdesk-page.png`}
        />
        <meta key="ogsitename" property="og:site_name" content={`${originUrl}/`} />
        <meta
          key="ogdescription"
          property="og:description"
          content="Enhance your Freshdesk customer support with our patented AI solution. Improve ticket processing speed by 80%, boost resolution rates by 68%, and elevate team efficiency by 80%. CoSupport AI assistant is trained on your data, ensuring personalized and accurate responses for exceptional customer service."
        />
        <meta key="viewport" name="viewport" content="initial-scale=1.0, width=device-width" />
        <script
          key="intlTelInputminjs"
          defer
          src="/js/intl-tel-input/js/intlTelInput.min.js"
        ></script>
        <Script
          id="googletagmanager3"
          key="googletagmanager3"
          strategy="beforeInteractive"
          src="https://www.googletagmanager.com/gtag/js?id=AW-11237889467"
        />
        <Script
          id="googletagmanager4"
          key="googletagmanager4"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', 'AW-11237889467');
          `,
          }}
        />
        <Script
          id="googletagmanager5"
          key="googletagmanager5"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `
            (function (w, d, s, l, i) {
            w[l] = w[l] || [];
            w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
            var f = d.getElementsByTagName(s)[0],
              j = d.createElement(s),
              dl = l != 'dataLayer' ? '&l=' + l : '';
            j.async = true;
            j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
            f.parentNode.insertBefore(j, f);
          })(window, document, 'script', 'dataLayer', 'GTM-WXHFTKL9');
          `,
          }}
        />
        <Script
          id="dealfront"
          key="dealfront"
          dangerouslySetInnerHTML={{
            __html: `(function(ss,ex){ window.ldfdr=window.ldfdr||function(){(ldfdr._q=ldfdr._q||[]).push([].slice.call(arguments));}; (function(d,s){ fs=d.getElementsByTagName(s)[0]; function ce(src){ var cs=d.createElement(s); cs.src=src; cs.async=1; fs.parentNode.insertBefore(cs,fs); }; ce('https://sc.lfeeder.com/lftracker_v1_'+ss+(ex?'_'+ex:'')+'.js'); })(document,'script'); })('JMvZ8gnv0VPa2pOd');`,
          }}
        />
        <Script
          id="apollo"
          key="apollo"
          strategy="lazyOnload"
          dangerouslySetInnerHTML={{
            __html: `!function(){var e,a;e=Math.random().toString(36).substring(7),(a=document.createElement("script")).src="https://assets.apollo.io/micro/website-tracker/tracker.iife.js?nocache="+e,a.async=!0,a.defer=!0,a.onload=function(){window.trackingFunctions.onLoad({appId:"66bb4c503be771078edff8ab"})},document.head.appendChild(a)}();`,
          }}
        />
      </Head>
      {loading && <Loader />}
      <Header pathname={PATH_INTEGRATION.INTEGRATION_FRESHDESK} />
      <SolveTickets isMobile={isMobile} />
      <MediaRecognition isDark={false} className="!bg-transparent lg:hidden block" />
      <SecondSection />
      <AiForFreshdesk />
      <BoostYourSupport />
      <TransparentPricing isMobile={isMobile} />
      <AiAssistantFreshdesk />
      <IntegrationCRM isMobile={isMobile} />
      <ConnectRealGenerative />
      <EasyStartWithFreshdesk />
      <EnsuringDataPrivacy />
      <PatentedTechnology />
      <FAQFreshdesk isMobile={isMobile} />
      <SuperChargeWithAgent isMobile={isMobile} />
      <Footer />
    </>
  );
}
export const getServerSideProps = async (
  context: GetServerSidePropsContext
): Promise<{
  props: FreshdeskIntegrationProps;
}> => {
  const originUrl: string = process.env.ORIGIN_URL || 'https://cosupport.ai';
  const userAgent = context.req.headers['user-agent'] ? context.req.headers['user-agent'] : '';
  const { isMobile } = getSelectorsByUserAgent(userAgent);

  return {
    props: { isMobile, originUrl },
  };
};
